import React, {useEffect, useState} from "react";
import {
    Table, Button, Modal, Form, Input, Popconfirm, message, Space, Alert,
    Breadcrumb, Layout, Switch, Select, Upload, DatePicker, InputNumber, Tag
} from "antd";
import Api from "../api";
import {Link} from "react-router-dom";
import dayjs from "dayjs";
import {PictureOutlined, PlusOutlined} from "@ant-design/icons";

const {Content} = Layout;
const {TextArea} = Input;
const {Option} = Select;

const BannersView = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [form] = Form.useForm();
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [billingPlans, setBillingPlans] = useState([]);
    const [showOnlyActive, setShowOnlyActive] = useState(true);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
    });

    useEffect(() => {
        fetchBanners(1);
    }, [showOnlyActive]);

    // Fetch all banners
    const fetchBanners = (page = 1) => {
        setLoading(true);

        const params = new URLSearchParams({
            page,
        });

        if (showOnlyActive) {
            params.append('active', 'true');
        }

        Api.get(`/admin/banners?${params.toString()}`)
            .then((res) => {
                setBanners(res.data.data);
                setPagination({
                    current: res.data.current_page,
                    total: res.data.total,
                    pageSize: res.data.per_page,
                });
            })
            .catch(() => {
                message.error("Failed to load banners");
            })
            .finally(() => setLoading(false));
    };

    const handleTableChange = (pagination) => {
        fetchBanners(pagination.current);
    };


    // Fetch available billing plans
    const fetchBillingPlans = () => {
        Api.get("/admin/banners/available-billing")
            .then((res) => {
                const plans = res.data.data || res.data || [];
                setBillingPlans(Array.isArray(plans) ? plans : []);
            })
            .catch((err) => {
                message.error("Failed to load billing plans");
            });
    };

    useEffect(() => {
        fetchBanners(1);
        fetchBillingPlans();
    }, []);

    // Handle local image selection (no upload yet)
    const handleImageChange = (info) => {
        const file = info.file.originFileObj || info.file;

        // Validate file type
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
            return;
        }

        // Validate file size (max 5MB)
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Image must be smaller than 5MB!');
            return;
        }

        // Store file for later upload
        setImageFile(file);

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    // Add or update banner - send all data as FormData
    const handleSave = () => {
        form.validateFields().then(async (values) => {
            try {
                const formData = new FormData();

                // Laravel: 'image' => 'sometimes|image'
                if (imageFile) {
                    formData.append('image', imageFile);
                }

                // Required
                formData.append('location', values.location);
                formData.append('title', values.title);

                // Nullable string fields
                if (values.description) formData.append('description', values.description);
                if (values.link) formData.append('link', values.link);

                // Dates: nullable|date (ends_at after_or_equal:starts_at)
                // Send local time as-is; toISOString() would shift to UTC and store wrong hours
                if (values.starts_at) {
                    formData.append('starts_at', values.starts_at.format('YYYY-MM-DD HH:mm:ss'));
                }
                if (values.ends_at) {
                    formData.append('ends_at', values.ends_at.format('YYYY-MM-DD HH:mm:ss'));
                }

                // Integer threshold: nullable|integer|min:0
                if (values.days_left_threshold !== undefined && values.days_left_threshold !== null && values.days_left_threshold !== '') {
                    formData.append('days_left_threshold', String(values.days_left_threshold));
                }

                // Boolean: sometimes|boolean
                if (typeof values.is_active !== 'undefined') {
                    formData.append('is_active', values.is_active ? '1' : '0');
                }

                // membership_conditions: nullable|array with allowed values
                if (Array.isArray(values.membership_conditions) && values.membership_conditions.length > 0) {
                    values.membership_conditions.forEach((v, i) => {
                        formData.append(`membership_conditions[${i}]`, v);
                    });
                }

                // billing_plan_ids: sometimes|array
                if (Array.isArray(values.billing_plan_ids) && values.billing_plan_ids.length > 0) {
                    values.billing_plan_ids.forEach((id, i) => {
                        formData.append(`billing_plan_ids[${i}]`, id);
                    });
                }

                // billing_plan_days: nullable|integer|min:1
                if (values.billing_plan_days !== undefined && values.billing_plan_days !== null && values.billing_plan_days !== '') {
                    formData.append('billing_plan_days', String(values.billing_plan_days));
                }

                // metadata: nullable|array (send JSON)
                formData.append('metadata', JSON.stringify(values.metadata || {}));

                if (editingBanner) {
                    formData.append('_method', 'PUT');
                    await Api.post(`/admin/banners/${editingBanner.id}`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    message.success("Banner updated");
                } else {
                    await Api.post("/admin/banners", formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    message.success("Banner added");
                }

                fetchBanners();
                setOpenModal(false);
                form.resetFields();
                setEditingBanner(null);
                setImageFile(null);
                setImagePreview(null);

            } catch (error) {
                message.error(editingBanner ? "Update failed" : "Add failed");
            }
        });
    };
    // Delete banner
    const handleDelete = (id) => {
        Api.delete(`/admin/banners/${id}`)
            .then(() => {
                message.success("Banner deleted");
                fetchBanners();
            })
            .catch(() => message.error("Delete failed"));
    };

    // Toggle banner active status
    const handleToggleStatus = (id, isActive) => {
        const banner = banners.find(b => b.id === id);
        if (!banner) return;

        // Send only field(s) that change – boolean respected by Laravel
        Api.put(`/admin/banners/${id}`, {
            is_active: isActive ? 1 : 0,
        })
            .then(() => {
                message.success(`Banner ${isActive ? "activated" : "deactivated"}`);
                setBanners(prev =>
                    prev.map(b =>
                        b.id === id ? { ...b, is_active: isActive ? 1 : 0 } : b
                    )
                );
            })
            .catch(() => {
                message.error("Failed to update status");
            });
    };



    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            width: 60,
        },
        {
            title: "Image",
            dataIndex: "image_url",
            width: 100,
            render: (url) => url ? (
                <img
                    src={url}
                    alt="banner"
                    style={{
                        width: 80,
                        height: 50,
                        objectFit: 'cover',
                        borderRadius: '4px'
                    }}
                />
            ) : '-'
        },
        {
            title: "Title",
            dataIndex: "title",
            width: 200,
        },
        {
            title: "Location",
            dataIndex: "location",
            width: 120,
            render: (location) => <Tag color="blue">{location}</Tag>
        },
        {
            title: "Conditions",
            key: "conditions",
            width: 260,
            render: (_, record) => (
                <Space direction="vertical" size="small">
                    {typeof record.days_left_threshold === 'number' && (
                        <Tag color="orange">Days left ≤ {record.days_left_threshold}</Tag>
                    )}
                    {Array.isArray(record.membership_conditions) && record.membership_conditions.map((c) => (
                        <Tag key={c} color="purple">{c}</Tag>
                    ))}
                </Space>
            )
        },
        {
            title: "Starts",
            dataIndex: "starts_at",
            width: 140,
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '—'
        },
        {
            title: "Ends",
            dataIndex: "ends_at",
            width: 140,
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '—'
        },
        {
            title: "Clicks",
            dataIndex: "click_count",
            width: 80,
            render: (value) => value ?? 0
        },
        {
            title: "Conversions",
            dataIndex: "conversion_count",
            width: 110,
            render: (value) => value ?? 0
        },
        {
            title: "Revenue",
            dataIndex: "revenue",
            width: 110,
            render: (value) => (value ?? 0) + ' €'
        },
        {
            title: "CTR",
            width: 80,
            render: (_, record) => {
                const clicks = record.click_count || 0;
                const conversions = record.conversion_count || 0;

                if (!clicks) return "0%";
                const ctr = (conversions / clicks) * 100;
                return ctr.toFixed(1) + "%";
            }
        },
        {
            title: "Active",
            dataIndex: "is_active",
            width: 80,
            render: (value, record) => (
                <Switch
                    checked={!!value}
                    onChange={(checked) => handleToggleStatus(record.id, checked)}
                />
            ),
        },
        {
            title: "Actions",
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        onClick={() => {
                            setEditingBanner(record);
                            const formData = {
                                title: record.title || '',
                                description: record.description || '',
                                location: record.location || undefined,
                                link: record.link || '',
                                is_active: !!record.is_active,
                                starts_at: record.starts_at ? dayjs(record.starts_at) : null,
                                ends_at: record.ends_at ? dayjs(record.ends_at) : null,
                                days_left_threshold: typeof record.days_left_threshold === 'number' ? record.days_left_threshold : null,
                                membership_conditions: Array.isArray(record.membership_conditions) ? record.membership_conditions : [],
                                billing_plan_ids: Array.isArray(record.billing_plan_ids) ? record.billing_plan_ids.map(id => Number(id)) : [],
                                billing_plan_days: typeof record.billing_plan_days === 'number' ? record.billing_plan_days : null,
                                // metadata is free-form object; keep it as-is if backend returns it
                                metadata: record.metadata || {},
                            };
                            form.setFieldsValue(formData);
                            setImagePreview(record.image_url);
                            setImageFile(null);
                            setOpenModal(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure delete this banner?"
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button type="link" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Content style={{margin: '0 16px'}}>
            <Breadcrumb style={{margin: '16px 0'}}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item><Link to="/banners">Banners</Link></Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <Alert
                    showIcon={true}
                    style={{marginBottom: 16}}
                    message={
                        <div>
                            Create and manage personalized banners for users based on dates, membership status, and more.
                        </div>
                    }
                />

                <Space style={{ marginBottom: 16 }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingBanner(null);
                            form.resetFields();
                            setImageFile(null);
                            setImagePreview(null);
                            setOpenModal(true);
                        }}
                    >
                        Add Banner
                    </Button>

                    <Space>
                        <span>Show only active</span>
                        <Switch
                            checked={showOnlyActive}
                            onChange={(checked) => {
                                setShowOnlyActive(checked);
                            }}
                        />
                    </Space>
                </Space>


                <Table
                    size="small"
                    rowKey="id"
                    columns={columns}
                    dataSource={banners}
                    loading={loading}
                    scroll={{ x: 1300 }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: false, // fixed at 20
                    }}
                    onChange={handleTableChange}
                />

                <Modal
                    title={editingBanner ? "Edit Banner" : "Add Banner"}
                    open={openModal}
                    onOk={handleSave}
                    onCancel={() => {
                        setOpenModal(false);
                        setEditingBanner(null);
                        form.resetFields();
                        setImageFile(null);
                        setImagePreview(null);
                    }}
                    okText="Save"
                    width={700}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            label="Banner Image"
                        >
                            <Upload
                                listType="picture-card"
                                showUploadList={false}
                                beforeUpload={() => false}
                                onChange={handleImageChange}
                                accept="image/*"
                            >
                                {imagePreview ? (
                                    <img
                                        src={imagePreview}
                                        alt="banner"
                                        style={{width: '100%', maxHeight: '200px', objectFit: 'contain'}}
                                    />
                                ) : (
                                    <div>
                                        <PictureOutlined />
                                        <div style={{marginTop: 8}}>Upload</div>
                                    </div>
                                )}
                            </Upload>
                            <div style={{color: '#999', fontSize: '12px', marginTop: '8px'}}>
                                Recommended size: 1200x400px. Max size: 5MB
                            </div>
                        </Form.Item>

                        <Form.Item
                            name="location"
                            label="Display Location"
                            rules={[
                                { required: true, message: "Please select location" },
                                { max: 100, message: "Max 100 characters" }
                            ]}
                        >
                            <Select placeholder="Select banner location" showSearch optionFilterProp="children">
                                <Option value="mobile">App - Mobile (meal plan, blog)</Option>
                                <Option value="sidebar">App - Desktop (top left sidebar)</Option>
                                <Option value="desktop">Public - Desktop (posts, recipes)</Option>
                                <Option value="navbar">Public - Navbar Button Top</Option>
                                <Option value="fixed-button">Public - Floating Buy Now Button</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="title"
                            label="Title"
                            rules={[
                                { required: true, message: "Please enter title" },
                                { max: 255, message: "Max 255 characters" }
                            ]}
                        >
                            <Input placeholder="Enter banner title"/>
                        </Form.Item>

                        <Form.Item
                            name="description"
                            label="Description"
                            rules={[{ type: 'string' }]}
                        >
                            <TextArea rows={3} placeholder="Enter banner description"/>
                        </Form.Item>

                        <Form.Item
                            name="link"
                            label="Link URL"
                            rules={[
                                { type: 'url', message: 'Please enter a valid URL' },
                                { max: 2048, message: 'Max 2048 characters' }
                            ]}
                        >
                            <Input placeholder="https://example.com"/>
                        </Form.Item>

                        <h4 style={{marginTop: 20, marginBottom: 10}}>Timing</h4>

                        <Form.Item name="starts_at" label="Starts at">
                            <DatePicker
                                showTime
                                format="YYYY-MM-DD HH:mm"
                                style={{width: '100%'}}
                            />
                        </Form.Item>

                        <Form.Item name="ends_at" label="Ends at" dependencies={['starts_at']} rules={[
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    const start = getFieldValue('starts_at');
                                    if (!value || !start || dayjs(value).isAfter(dayjs(start)) || dayjs(value).isSame(dayjs(start))) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('End must be after or equal to start'));
                                },
                            }),
                        ]}>
                            <DatePicker
                                showTime
                                format="YYYY-MM-DD HH:mm"
                                style={{width: '100%'}}
                            />
                        </Form.Item>

                        <h4 style={{marginTop: 20, marginBottom: 10}}>Audience Conditions</h4>

                        <Form.Item
                            name="days_left_threshold"
                            label="Days left threshold"
                            tooltip="Show when user's subscription has less or equal days left than this number"
                            rules={[{ type: 'number', min: 0 }]}
                        >
                            <InputNumber min={0} max={365} placeholder="e.g., 7" style={{width: '100%'}}/>
                        </Form.Item>

                        <Form.Item
                            name="membership_conditions"
                            label="Membership conditions"
                            tooltip="Control visibility based on membership"
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Select membership conditions"
                                options={[
                                    { value: 'hide_inactive', label: 'Hide Inactive' },
                                    { value: 'show_only_inactive', label: 'Show only Inactive' },
                                    { value: 'hide_trial', label: 'Hide Trial' },
                                    { value: 'show_only_trial', label: 'Show only Trial' },
                                    { value: 'hide_regular', label: 'Hide regular' },
                                    { value: 'show_only_regular', label: 'Show only regular' },
                                    { value: 'hide_recurring', label: 'Hide recurring' },
                                    { value: 'show_only_recurring', label: 'Show only recurring' },
                                    { value: 'hide_vip', label: 'Hide VIP' },
                                    { value: 'show_only_vip', label: 'Show only VIP' },
                                ]}
                            />
                        </Form.Item>

                        <Form.Item
                            name="billing_plan_ids"
                            label="Show for billing plans"
                            tooltip="Select billing plans that should see this banner"
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Select billing plans to show banner from"
                                loading={billingPlans.length === 0}
                            >
                                {billingPlans.map((plan) => {
                                    return (
                                        <Option key={plan.plan_id} value={Number(plan.plan_id)}>
                                            {plan.title}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="billing_plan_days"
                            label="Show for after billing plan days"
                            tooltip="Show banner for users with selected billing plans for this many days after purchase"
                            rules={[{ type: 'number', min: 1 }]}
                        >
                            <InputNumber min={1} placeholder="e.g., 30" style={{width: '100%'}}/>
                        </Form.Item>

                        <Form.Item
                            name={['metadata', 'blacklist_urls']}
                            label="Hide banner on these URLs"
                            tooltip="Banner will not show on pages matching these URLs"
                        >
                            <Select mode="tags" placeholder="e.g. /partnerid/123" />
                        </Form.Item>

                        <Form.Item
                            name="is_active"
                            label="Active"
                            valuePropName="checked"
                            initialValue={true}
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </Content>
    );
};

export default BannersView;